import { useMemo, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { IconPhoto } from '@tabler/icons-react';
import {
  Badge,
  Breadcrumb,
  EmptyState,
  ErrorState,
  Metric,
  Panel,
  Progress,
  cn,
  formatDate,
  formatMoney,
  formatNumber,
  formatPercentage,
} from 'escrowbuild-ui';

import {
  usePublishedEvidence,
  usePublishedPublications,
  useReports,
  useScheduleOf,
  useT,
  type AppTranslationKey,
} from '@/i18n';
import { findProject, projectTotals, type ReportRecord } from '@/data/domain';

const KIND_LABEL: Record<ReportRecord['kind'], AppTranslationKey> = {
  period: 'report.kind.period',
  phase: 'report.kind.phase',
  final: 'report.kind.final',
};

/**
 * One generated report, previewed.
 *
 * The whole page reads from the same derived helpers the project dashboard uses
 * — `projectTotals`, the schedule and the published-only filters — so a report
 * cannot quote a figure that disagrees with the screen it was generated from.
 * The published filters are the point of the demo, not a detail: the investor
 * portal's central rule is that only approved content becomes visible, and this
 * page has to show fewer updates and photos than the internal register would.
 */
export function ReportDetailPage() {
  const { reportId } = useParams();
  const t = useT();

  /*
     Every hook runs before the not-found return below, because a hook cannot run
     after a conditional one. The empty development the branch passes selects
     nothing, which is what that branch wants — and it renders an error state
     rather than any of these lists.
  */
  const reports = useReports();
  const report = reports.find((entry) => entry.id === reportId);
  const projectId = report?.projectId ?? '';
  const phases = useScheduleOf(projectId);
  const evidence = usePublishedEvidence(projectId);

  /** Newest first, over a COPY: the hook memoises its array, and sorting that in
   *  place would re-order what every other reader of that memo sees. */
  const released = usePublishedPublications(projectId);
  const publications = useMemo(
    () => [...released].sort((a, b) => (b.publishedAt ?? '').localeCompare(a.publishedAt ?? '')),
    [released],
  );

  if (!report) {
    return (
      <Panel padding="lg" title={t('report.notFoundTitle')}>
        <div className="flex flex-col gap-6">
          <ErrorState
            variant="page"
            title={t('report.notFoundHeading')}
            message={t('report.notFoundMessage', { id: reportId ?? '' })}
          />
          <RegisterLink>{t('report.backToRegister')}</RegisterLink>
        </div>
      </Panel>
    );
  }

  const project = findProject(report.projectId);
  const totals = projectTotals(report.projectId);
  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb
        items={[{ label: t('reports.title'), href: '/reports' }, { label: report.title }]}
      />

      <header>
        <h1 className="font-display text-3xl font-semibold uppercase tracking-title text-heading">
          {report.title}
        </h1>
        <p className="mt-1 text-sm text-muted">
          {project?.name ?? t('report.unknownDevelopment')} · {report.scope} · {report.period}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Badge tone={KIND_BADGE_TONE[report.kind]} variant="subtle">
            {t(KIND_LABEL[report.kind])}
          </Badge>
          <Badge tone={report.published ? 'success' : 'neutral'} variant="subtle">
            {report.published ? t('status.publication.published') : t('status.publication.draft')}
          </Badge>
          <Badge tone="neutral" variant="subtle">
            {report.pages > 0
              ? t('report.pages', { count: report.pages, pages: formatNumber(report.pages) })
              : t('reports.notGenerated')}
          </Badge>
        </div>
      </header>

      {report.pages === 0 ? (
        <EmptyState
          variant="page"
          title={t('report.notGeneratedTitle')}
          description={t('report.notGeneratedBody')}
          action={<RegisterLink>{t('report.backToRegister')}</RegisterLink>}
        />
      ) : (
        <>
          <Panel
            title={t('report.financialTitle')}
            description={t('report.financialDescription')}
          >
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <Metric
                label={t('report.budgeted')}
                value={totals.budgeted}
                format="money"
                hint={t('report.budgetedHint')}
              />
              <Metric
                label={t('project.shell.spent')}
                value={totals.actual}
                format="money"
                hint={t('report.spentHint')}
              />
              <Metric
                label={t('project.shell.variance')}
                value={totals.varianceStarted}
                format="money"
                tone={totals.varianceStarted <= 0 ? 'success' : 'danger'}
                // Share of the started budget, and the started basis rather than
                // the whole-life one: a report is the document an investor
                // checks, so its headline number must compare spend with the
                // budget for work actually ordered.
                delta={
                  totals.startedBudgeted === 0
                    ? 0
                    : (totals.varianceStarted / totals.startedBudgeted) * 100
                }
                deltaFormat="percentage"
                trend={totals.varianceStarted <= 0 ? 'down' : 'up'}
                hint={t('report.varianceHint', {
                  amount: formatMoney(totals.startedBudgeted),
                })}
              />
              <Metric
                label={t('report.unspent')}
                value={totals.available}
                format="money"
                tone="info"
                hint={t('report.unspentHint')}
              />
            </div>

            <div className="mt-6">
              <Progress
                value={totals.actual}
                max={totals.budgeted}
                showValue
                tone="primary"
                label={t('report.spendAgainstBudget', { title: report.title })}
              />
            </div>
          </Panel>

          <PhaseTable phases={phases} totals={totals} />

          <section className="flex flex-col gap-4">
            <div>
              <h2 className="font-display text-2xl font-semibold uppercase tracking-title text-heading">
                {t('report.updatesHeading')}
              </h2>
              {/* The platform's central rule, stated where the count is read: an
                  update that is still draft or in review is simply not here, and
                  that is the reason this list is shorter than the internal queue. */}
              <p className="mt-1 max-w-3xl text-sm text-muted">{t('report.updatesDescription')}</p>
            </div>

            {publications.length === 0 ? (
              <EmptyState
                variant="cards"
                title={t('report.updatesEmptyTitle')}
                description={t('report.updatesEmptyBody')}
              />
            ) : (
              publications.map((publication) => (
                <Panel
                  key={publication.id}
                  headingLevel={3}
                  title={publication.title}
                  description={publication.period}
                  headerActions={
                    <Badge tone="success" variant="subtle">
                      {t('report.publishedOn', {
                        date: formatDate(publication.publishedAt, { style: 'medium' }),
                      })}
                    </Badge>
                  }
                >
                  <p className="max-w-3xl text-sm text-body">{publication.body}</p>
                  <dl className="mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
                    <ReportMeta label={t('report.preparedBy')} value={publication.preparedBy} />
                    <ReportMeta label={t('report.approvedBy')} value={publication.approvedBy ?? '—'} />
                    <ReportMeta label={t('report.photos')} value={formatNumber(publication.photos)} />
                    <ReportMeta
                      label={t('report.documents')}
                      value={formatNumber(publication.documents)}
                    />
                  </dl>
                </Panel>
              ))
            )}
          </section>

          <Panel
            title={t('report.evidenceTitle')}
            description={t('report.evidenceDescription', { count: evidence.length })}
          >
            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {evidence.map((item) => (
                <li
                  key={item.id}
                  className="flex flex-col gap-2 rounded-xs border border-line-subtle bg-surface-muted p-3"
                >
                  <IconPhoto size={20} aria-hidden="true" className="text-muted" />
                  <p className="text-sm font-medium text-heading">{item.caption}</p>
                  <p className="mt-auto text-2xs text-muted">
                    {formatDate(item.date, { style: 'medium' })} · {item.capturedBy}
                  </p>
                </li>
              ))}
            </ul>
          </Panel>
        </>
      )}
    </div>
  );
}

const KIND_BADGE_TONE: Record<ReportRecord['kind'], 'info' | 'neutral' | 'warning'> = {
  period: 'info',
  phase: 'neutral',
  final: 'warning',
};

function PhaseTable({
  phases,
  totals,
}: {
  phases: ReturnType<typeof useScheduleOf>;
  totals: ReturnType<typeof projectTotals>;
}) {
  const t = useT();
  const usedPercent = (budgeted: number, actual: number) => (budgeted > 0 ? (actual / budgeted) * 100 : 0);

  return (
    <Panel title={t('report.phasesTitle')} description={t('report.phasesDescription')}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">{t('report.phasesCaption')}</caption>
          <thead>
            <tr className="border-b border-line-strong text-left">
              <th scope="col" className="px-3 py-2 font-display text-2xs font-semibold uppercase tracking-label text-muted">
                {t('report.column.phase')}
              </th>
              <th scope="col" className="px-3 py-2 font-display text-2xs font-semibold uppercase tracking-label text-muted">
                {t('common.status')}
              </th>
              <th scope="col" className="px-3 py-2 text-right font-display text-2xs font-semibold uppercase tracking-label text-muted">
                {t('report.column.budgeted')}
              </th>
              <th scope="col" className="px-3 py-2 text-right font-display text-2xs font-semibold uppercase tracking-label text-muted">
                {t('report.column.actual')}
              </th>
              <th scope="col" className="px-3 py-2 text-right font-display text-2xs font-semibold uppercase tracking-label text-muted">
                {t('report.column.variance')}
              </th>
              <th scope="col" className="px-3 py-2 text-right font-display text-2xs font-semibold uppercase tracking-label text-muted">
                {t('report.column.used')}
              </th>
            </tr>
          </thead>
          <tbody>
            {phases.map((phase) => {
              const variance = phase.actual - phase.budgeted;
              return (
                <tr key={phase.id} className="border-b border-line-subtle">
                  <th scope="row" className="px-3 py-2 text-left font-normal text-body">
                    {phase.name}
                  </th>
                  <td className="px-3 py-2">
                    <Badge tone={PHASE_STATUS_TONE[phase.status]} variant="subtle">
                      {t(PHASE_STATUS_LABEL[phase.status])}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-body">
                    {formatMoney(phase.budgeted)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-body">
                    {formatMoney(phase.actual)}
                  </td>
                  <td
                    className={cn(
                      'px-3 py-2 text-right tabular-nums',
                      variance <= 0 ? 'text-success' : 'text-danger',
                    )}
                  >
                    {formatMoney(variance)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-body">
                    {/* A phase with no budget has no percentage of it consumed,
                        so it shows a dash rather than a misleading 0%. */}
                    {phase.budgeted === 0
                      ? '—'
                      : formatPercentage(usedPercent(phase.budgeted, phase.actual), { precision: 0 })}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t border-line-strong text-heading">
              <td className="px-3 py-3 font-medium">{t('common.total')}</td>
              <td className="px-3 py-3 text-muted">
                {t('report.phaseCount', { count: phases.length })}
              </td>
              <td className="px-3 py-3 text-right font-medium tabular-nums">
                {formatMoney(totals.budgeted)}
              </td>
              <td className="px-3 py-3 text-right font-medium tabular-nums">
                {formatMoney(totals.actual)}
              </td>
              <td
                className={cn(
                  'px-3 py-3 text-right font-medium tabular-nums',
                  totals.variance <= 0 ? 'text-success' : 'text-danger',
                )}
              >
                {formatMoney(totals.variance)}
              </td>
              <td className="px-3 py-3 text-right font-medium tabular-nums">
                {formatPercentage(usedPercent(totals.budgeted, totals.actual), { precision: 0 })}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Panel>
  );
}

const PHASE_STATUS_TONE: Record<string, 'success' | 'info' | 'neutral'> = {
  completed: 'success',
  'in-progress': 'info',
  planned: 'neutral',
};

const PHASE_STATUS_LABEL: Record<string, AppTranslationKey> = {
  completed: 'status.phase.completed',
  'in-progress': 'status.phase.inProgress',
  planned: 'status.phase.planned',
};

function ReportMeta({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className="font-display text-2xs font-semibold uppercase tracking-label text-muted">{label}</dt>
      <dd className="mt-0.5 text-sm text-body">{value}</dd>
    </div>
  );
}

/**
 * A router link wearing the raised primary `Button`'s clothes. `Button` renders a
 * real `<button>`, and a navigation belongs in an `<a>`, so the classes are
 * mirrored onto the `Link` rather than nesting one inside the other.
 */
function RegisterLink({ children }: { children: ReactNode }) {
  return (
    <Link
      to="/reports"
      className={cn(
        'dhd-target relative inline-flex h-10 select-none items-center justify-center gap-2',
        'rounded-xs border border-primary bg-primary px-5 font-display text-xs font-semibold',
        'uppercase tracking-label text-on-primary shadow-hard',
        'transition-[transform,background-color,color,box-shadow,border-color,filter] duration-[var(--dur-fast)] ease-[var(--ease-spring)]',
        'hover:-translate-x-[2px] hover:-translate-y-[2px] hover:bg-primary-hover hover:shadow-[8px_8px_0_var(--text-heading)]',
        'active:translate-x-[3px] active:translate-y-[3px] active:shadow-none',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
      )}
    >
      {children}
    </Link>
  );
}
